import { useMemo, useContext, useRef, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { isASavedSearch } from '../../utils/saved-searches/compare-save-searches'
import { SavedSearch } from '../../patterns/saved-searches/save-search'
import { FacetName } from '../../__generated__/fed-graphql-types'
import { UserLocationContext } from '../../contexts/user-location-context'
import { setSearchId } from '../../utils/set-search-id'
import { useFirstRender } from '../use-first-render/use-first-render'

export const addPostcodeIfNotInUrl = (searchUrl: string, postcode?: string): string => {
    const hasPostcodeParam = searchUrl.includes(`${FacetName.postcode}=`)

    if (hasPostcodeParam) {
        return searchUrl
    }

    const hasQueryString = searchUrl.includes('?')
    const queryStringSeparator = hasQueryString ? '&' : '?'

    return `${searchUrl}${queryStringSeparator}${FacetName.postcode}=${postcode}`
}

const usePrevious = (value: string): string | undefined => {
    const ref = useRef<string | undefined>(undefined)
    useEffect(() => {
        ref.current = value
    })
    return ref.current
}

export const hasParamChangedThatIsNotPage = (prevSearch: string | undefined, currSearch: string): boolean => {
    const removePageParam = (search: string): string => {
        return search
            .split('&')
            .map((param, index) => (index === 0 ? param.slice(1) : param))
            .filter((param) => !param.includes('page='))
            .sort((a, b) => a.localeCompare(b))
            .join('&')
    }
    const prevSearchWithoutPage = prevSearch ? removePageParam(prevSearch) : ''
    const currSearchWithoutPage = removePageParam(currSearch)
    return prevSearchWithoutPage !== currSearchWithoutPage
}

const handleSearchIdUpdate = (paramChangedButNotPage: boolean) => {
    if (paramChangedButNotPage) {
        setSearchId()
    }
}

export const useSaveSearchCompare = (savedSearchList: ReadonlyArray<SavedSearch>) => {
    const { pathname, search } = useLocation()
    const { postcode } = useContext(UserLocationContext)
    const isFirstRender = useFirstRender()

    const previousSearch = usePrevious(search)

    const found = useMemo(() => {
        if (savedSearchList.length > 0) {
            const url = window.location.origin + pathname + search
            const urlWithPostcode = addPostcodeIfNotInUrl(url, postcode || '')

            try {
                // if page param is not present or is present but has not change, we update the search id
                const pageParamChanged = hasParamChangedThatIsNotPage(previousSearch, search)
                !isFirstRender && handleSearchIdUpdate(pageParamChanged)

                return isASavedSearch(savedSearchList, urlWithPostcode)
            } catch (error) {
                const errorMessage = (error as Record<string, string>).message
                console.error(`Error comparing saved searches: ${errorMessage}`)
            }
        }
        return false
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, pathname, savedSearchList, postcode, previousSearch])

    return {
        foundMatchingSavedSearch: found,
    }
}
